import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader, EmptyState, TimelineItem } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DoorClosed, PlusCircle, ExternalLink, Loader2, Check, FileText } from 'lucide-react';
import { fmtDate, fmtDateTime } from '@/lib/format';

const OFFBOARDING_REASONS = [
  'Resignation',
  'Termination',
  'End of contract',
  'Layoff / Redundancy',
  'Retirement',
  'Better opportunity',
  'Relocation',
  'Personal reasons',
  'Health reasons',
  'Higher studies',
  'Performance issues',
  'Absconding',
  'Mutual separation',
  'Other',
];

function NewOffboardingDialog({ companyId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', role: '', exit_date: '', reason: '', reason_note: '' });
  const submit = async () => {
    if (!f.name || !f.email || !f.role) return toast.error('Name, email, role required');
    if (!f.reason) return toast.error('Please pick a reason');
    try {
      const finalReason = f.reason === 'Other' && f.reason_note ? f.reason_note : (f.reason_note ? `${f.reason} — ${f.reason_note}` : f.reason);
      await api.post('/offboardings', { ...f, reason: finalReason, company_id: companyId });
      toast.success('Offboarding initiated'); setOpen(false); onCreated && onCreated();
    } catch (e) { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-offboarding-button"><PlusCircle className="h-4 w-4 mr-2" />Add offboarding</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Initiate offboarding</DialogTitle><DialogDescription>Track exit checklist, generate letters and log audit trail.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="off-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Role</Label><Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Exit date</Label><Input type="date" value={f.exit_date} onChange={(e) => setF({ ...f, exit_date: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Reason *</Label>
              <Select value={f.reason} onValueChange={(v) => setF({ ...f, reason: v })}>
                <SelectTrigger data-testid="off-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent>{OFFBOARDING_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Additional detail (optional)</Label><Input value={f.reason_note} onChange={(e) => setF({ ...f, reason_note: e.target.value })} placeholder="Any specific note for the reason" data-testid="off-reason-note" /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Initiate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OffboardingPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [current, setCurrent] = useState(null);
  const [letterKind, setLetterKind] = useState('relieving');
  const [drafting, setDrafting] = useState(false);

  const load = () => api.get(`/offboardings?company_id=${companyId}`).then((r) => setItems(r.data));
  useEffect(() => { api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)); load(); }, [companyId]);
  useEffect(() => { if (openId) api.get(`/offboardings/${openId}`).then((r) => setCurrent(r.data)); else setCurrent(null); }, [openId]);

  const toggle = async (item_id, completed) => { const r = await api.post(`/offboardings/${openId}/checklist/toggle`, { item_id, completed }); setCurrent(r.data); load(); };

  const draftLetter = async () => {
    setDrafting(true);
    try {
      const r = await api.post('/ai/draft-letter', { kind: letterKind, employee_name: current.name, role: current.role, company_name: company?.name, exit_date: current.exit_date });
      await api.post(`/offboardings/${openId}/letter`, { kind: letterKind, content: r.data.content });
      const w = window.open('', '_blank');
      w.document.write(`<pre style="font-family: -apple-system, system-ui; padding: 32px; white-space: pre-wrap; line-height: 1.6">${r.data.content}</pre>`);
      w.document.title = `${letterKind} letter - ${current.name}`;
      const r2 = await api.get(`/offboardings/${openId}`); setCurrent(r2.data);
    } catch (e) { toast.error('Failed'); }
    finally { setDrafting(false); }
  };

  const del = async (id) => { if (!window.confirm('Delete offboarding record?')) return; await api.delete(`/offboardings/${id}`); toast.success('Deleted'); load(); };

  return (
    <>
      <PageHeader
        title={`${company?.name || '…'} · Offboarding`}
        description="Exit checklist, timestamps and AI-drafted relieving/experience/no-dues letters."
        actions={<NewOffboardingDialog companyId={companyId} onCreated={load} />}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {items.length === 0 ? (
          <EmptyState icon={DoorClosed} title="No offboardings yet" description="Initiate an exit workflow for any exiting employee." />
        ) : (
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Role</TableHead><TableHead>Exit date</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((o) => (
                  <TableRow key={o.id} className="cursor-pointer" onClick={() => setOpenId(o.id)} data-testid={`offboarding-row-${o.id}`}>
                    <TableCell><p className="font-medium">{o.name}</p><p className="text-xs text-muted-foreground">{o.email}</p></TableCell>
                    <TableCell>{o.role}</TableCell>
                    <TableCell>{fmtDate(o.exit_date)}</TableCell>
                    <TableCell>{o.reason || '—'}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded ${o.status === 'completed' ? 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]' : 'bg-muted text-muted-foreground'}`}>{o.status}</span></TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); del(o.id); }} data-testid={`offboarding-delete-${o.id}`}>Delete</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {current && (
            <>
              <SheetHeader><SheetTitle className="font-display">{current.name}</SheetTitle><SheetDescription>{current.role} · Exit: {fmtDate(current.exit_date)}</SheetDescription></SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border bg-card p-4">
                  <h4 className="font-display font-semibold mb-3">Exit checklist</h4>
                  <ul className="space-y-2">
                    {(current.checklist || []).map((item) => (
                      <li key={item.id} className="flex items-start gap-3">
                        <Checkbox checked={item.completed} onCheckedChange={(v) => toggle(item.id, !!v)} data-testid={`off-checklist-${item.id}`} />
                        <div className="flex-1">
                          <p className={`text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>{item.label}</p>
                          {item.completed && <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Check className="h-3 w-3" /> {fmtDateTime(item.completed_at)} by {item.actor_name}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl border bg-card p-4">
                  <h4 className="font-display font-semibold mb-2 flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Letters</h4>
                  <div className="flex flex-wrap items-center gap-2">
                    {['relieving','experience','no_dues'].map((k) => (
                      <Button key={k} size="sm" variant={letterKind === k ? 'default' : 'outline'} onClick={() => setLetterKind(k)} className="capitalize" data-testid={`off-letter-${k}`}>{k.replace('_',' ')}</Button>
                    ))}
                    <Button size="sm" onClick={draftLetter} disabled={drafting} data-testid="off-letter-draft">{drafting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1" />}Draft & open</Button>
                  </div>
                  {current.letters && Object.keys(current.letters).length > 0 && (
                    <div className="text-xs text-muted-foreground mt-3">Saved letters: {Object.keys(current.letters).join(', ')}</div>
                  )}
                </div>
                <div className="rounded-xl border bg-card p-4">
                  <h4 className="font-display font-semibold mb-3">Audit trail</h4>
                  <div className="relative timeline-rail pl-1">
                    {(current.timeline || []).slice().reverse().map((ev) => <TimelineItem key={ev.id} event={ev} />)}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
